import { DOCS, IMG, LEGACY } from 'styles/theme';
import DELIVERY from '../fragments/delivery';

// The links the source FAQ doc carries inline. Ordering and program flows point
// at this site; subscriptions still go to the legacy page until it is ported.
const quiz = (label = 'quiz') => ({ label, to: '/quiz' });
const alaCarte = (label = 'à la carte') => ({ label, to: '/this-weeks-menu' });
const subscription = (label = 'subscription') => ({ label, href: `${LEGACY}/prepared-meal-subscription` });
const email = (address) => ({ label: address, href: `mailto:${address}` });

const page = {
  slug: 'faqs',
  title: 'FAQs',
  hero: {
    title: 'FAQs',
    image: IMG.menu,
  },
  blocks: [
    { type: 'heading', level: 'band', text: 'Getting Started' },
    {
      type: 'faq',
      items: [
        {
          q: 'What is Square Fare?',
          a: [
            [
              'Square Fare is a personalized meal delivery service that provides fresh, ready-to-eat meals tailored to your individual nutrition needs. Based on a quick intake ',
              quiz(),
              ', meals are customized to align with your calorie goals, macronutrient needs, and dietary preferences, so you can eat well without the hassle of planning, shopping, or cooking.',
            ],
          ],
        },
        {
          q: 'Who is it for?',
          a: [
            'Square Fare is for anyone who wants to eat healthier without the time and stress of cooking. It’s especially helpful for busy professionals, families, and individuals with specific health goals like weight loss, muscle gain, or improved overall wellness. Square Fare meals can also be tailored to support any health condition requiring specific nutrition.',
          ],
        },
        {
          q: 'How does it work?',
          a: [
            [
              'Getting started with Square Fare is simple. First, you complete a quick intake ',
              quiz(),
              ' to share your goals, preferences, and dietary needs. Based on your responses, Square Fare personalizes your meals to align with your calorie and macronutrient targets. Each week, you’ll receive freshly prepared, ready-to-eat meals delivered to your door—no cooking or planning required. You can manage your subscription, make adjustments, or skip weeks at any time.',
            ],
          ],
        },
        {
          q: 'What we do',
          a: [
            'We personalize your meals using a combination of your input and our nutrition team’s analysis:',
            {
              list: [
                ['Start with you: Complete our quick intake ', quiz(), ' or provide your own calorie, macro, or nutrition targets.'],
                'Build your profile: Our nutrition team reviews your information and creates a personalized nutrition profile, which we share with you.',
                'Customize each meal: We analyze every recipe and adjust portions of proteins, veggies, starches, and sauces to match your needs.',
                'Make it work for you: We account for allergies, dietary restrictions, and taste preferences with thoughtful substitutions.',
              ],
            },
            'This ensures every meal is aligned with your specific goals and requirements, without you having to plan or track.',
          ],
        },
        {
          q: 'What makes Square Fare different from other meal services?',
          a: [
            'Square Fare stands out through true personalization. Instead of offering one-size-fits-all meals (which would be the version 1.0 of what we do), each order is tailored to your calorie and macronutrient needs, dietary preferences, and restrictions. Meals are freshly prepared, gluten-free, and made without seed oils or added sugars, focusing on both quality ingredients and functional nutrition. Plus, they taste good!',
          ],
        },
        {
          q: 'Can Square Fare help with weight loss / muscle gain?',
          a: [
            'Yes. Square Fare is designed to support goals like weight loss, muscle gain, and body recomposition. Meals are portioned and balanced based on your individual needs, helping you stay consistent with your nutrition without needing to track or plan everything yourself. If you work with a registered dietitian (RD), Square Fare can also coordinate directly with your RD to ensure your personalized meals meet your needs.',
          ],
        },
        {
          q: 'How does Square Fare address my specific needs / health conditions?',
          a: [
            [
              'Square Fare starts with a personalized intake ',
              quiz(),
              ' to understand your goals, preferences, and dietary restrictions. Meals are then customized through portioning and ingredient selection, with accommodations for allergies, sensitivities, and lifestyle preferences.',
            ],
          ],
        },
      ],
    },

    { type: 'heading', level: 'band', text: 'Meals + Personalization' },
    {
      type: 'faq',
      items: [
        {
          q: 'What does a typical week of meals look like?',
          a: [
            'Menus rotate weekly and include a variety of balanced breakfast, lunch, and dinner options. Meals are designed to be nutrient-dense, satisfying, and diverse so you get a mix of proteins, vegetables, and whole-food ingredients without getting bored. Square Fare uses fresh, seasonal ingredients too which helps maximize flavor and nutrition.',
          ],
        },
        {
          q: 'When I place my order, do I have to specify the calories/allergies, etc for lunch and for dinner?',
          a: [
            [
              'If you took our intake ',
              quiz(),
              ' or gave us specs previously, you’re all set! (Unless you’d like us to update those specs). If you have further instructions, please provide them during checkout.',
            ],
          ],
        },
        {
          q: 'How many calories are in each meal?',
          a: [
            [
              'If you take our intake ',
              quiz(),
              ' or give us your specs, we will make each meal based on your individual needs. So, totally custom. If you don’t, then we will base your meal on 600 kcals | 60g carbs, 45g protein, 20g fat. We also make standard-sized plates of meats (16 oz), vegetables and starches.',
            ],
          ],
        },
        {
          q: 'Why don’t you share calorie and macro information by each meal on the website?',
          a: [
            [
              'We make every meal personalized and custom to your needs and so the calories (and other nutritional information) will be entirely dependent on what your specific needs are. When you take the intake ',
              quiz(),
              ', we will calculate what those nutritional needs are. Or you can tell us your specs, many people do when they work with a Registered Dietitian or Nutritionist. We then make each meal based on your individual needs. So, totally custom. If you don’t give us this information or buy a family platter, then we will base your meal on 600 kcals | 60g carbs, 45g protein, 20g fat.',
            ],
          ],
        },
        {
          q: 'How do you know how to tailor my meals?',
          a: [
            [
              'When you place your order, if you haven’t already, we ask that you take our intake ',
              quiz(),
              '. Or you can just tell us what specs you have if you already know (some of our customers work with nutritionists): calories, macros, or any other requirement. Our nutrition team analyzes your information and creates your nutritional profile. We share your results with you via email. These are the basis for how we portion out your food and make substitutions when needed.',
            ],
            [
              'We analyze the nutritional value of every ingredient from every one of our chef’s recipes. We group ingredients into categories: meat (animal or plant-based), veggies, starch, and sauce. And that’s where the magic happens. We adjust those category levels based on your individual needs so that your meal matches up to your nutritional requirements. We make substitutions if you have allergies or taste preferences. ',
              {
                label: 'Here’s a clip',
                href: 'https://www.instagram.com/reel/DEkTJPEupEH/',
              },
              ' that shows how the math works.',
            ],
          ],
        },
        {
          q: 'What’s the difference between ordering lunch or dinner?',
          a: [
            [
              'When you take our intake ',
              quiz(),
              ', we learn the food format that works best for you throughout your day. Specifying lunch or dinner helps us to customize your meals based on your needs for that time of day.',
            ],
          ],
        },
        {
          q: 'My plans changed and I won’t be eating my meal tonight. What should I do?',
          a: [
            'Since our meals are made fresh, you can freeze them for up to 2 months. To reheat, simply thaw overnight in the fridge before putting them in the microwave or oven as you normally would when you’re ready to eat.',
          ],
        },
        {
          q: 'How long do Square Fare meals keep?',
          a: [
            'Everything is made fresh with no preservatives. Food is best within 4 days of receiving it. We recommend that you eat your fish dishes and meals with raw veggies first. If you’d like to keep your meals longer, you can freeze them (since everything is made fresh).',
          ],
        },
        {
          q: 'How should I heat my meals?',
          a: [
            'Please refrigerate your meals as soon as possible after delivery. Keep them chilled until you’re ready to eat. For best quality and food safety, do not leave meals sitting out at room temperature for an extended period.',
            'It’s easy!',
            {
              ordered: true,
              list: [
                'Remove the sauce from the meal container (it can get very hot in the microwave!)',
                'Close the lid of the box. It helps keep the moisture of the dish.',
                'Microwave 1-1.5 mins',
                'Enjoy!',
              ],
            },
            'The meal containers are recyclable and microwaveable. Clients have also had success heating their food in the airfryer or the oven but this should be done using appropriate cookware.',
          ],
        },
        {
          q: 'How often does your menu change?',
          a: [
            'Our menu changes weekly. We keep the food exciting with new flavors and seasonal ingredients.',
          ],
        },
        {
          q: 'What is your pricing per meal?',
          a: [
            'Our meals start at $16.99. Prices increase based on the client’s selected protein, and dinners cost more than breakfast or lunch. All our ingredients are fresh, and all our meals are individually measured out to your specs. We are priced in line with takeout.',
          ],
        },
        {
          q: 'Where is the food made?',
          a: [
            'Our food is made right here in NYC! Our commercial kitchen is located in Long Island City.',
          ],
        },
        {
          q: 'Do you provide breakfast?',
          a: [
            'Yes! We offer breakfast options, both savory and sweet. Our sweet options get their sugar primarily from fruit. We occasionally use a touch of maple syrup in our overnight oats and can omit this. We do not add processed sugar to our food. In fact, we do not have any in the kitchen!',
          ],
        },
        {
          q: 'Do you also make snacks / desserts?',
          a: [
            'We do! Every week, we feature a seasonal fruit salad, as well as a savory and a sweet snack options.',
          ],
        },
        {
          q: 'Are you gluten-free?',
          a: [
            'Yes, we are a gluten-free service. We have a number of clients with celiac disease.',
          ],
        },
        {
          q: 'Can you tailor my food to my allergies?',
          a: [
            'Yes, we save all of your dietary constraints and will substitute ingredients as needed to ensure those are respected.',
          ],
        },
      ],
    },

    { type: 'heading', level: 'band', text: 'Pricing + Orders' },
    {
      type: 'faq',
      items: [
        {
          q: 'Can I pause or modify my subscription?',
          a: [
            [
              'Yes. Square Fare offers a flexible ',
              subscription(),
              ' model that allows you to pause, skip, or modify your plan as needed. You can also request adjustments to your meals before the weekly cutoff. For this, simply make changes on your dashboard or reach out to ',
              email('team@getsquarefare.com'),
              '.',
            ],
          ],
        },
        {
          q: 'Do I have to buy a subscription?',
          a: [
            [
              'While you can buy ',
              alaCarte('meals à la carte'),
              ', most clients choose ',
              subscription('a subscription'),
              ' for peace of mind (one less thing to worry about for the week!) and because it’s 10% cheaper than ordering à la carte. You can unsubscribe or skip a week at any time. Reach out with any questions: ',
              email('team@getsquarefare.com'),
              '.',
            ],
          ],
        },
        {
          q: 'Is there a minimum number of meals I should buy?',
          a: [
            [
              'No! You can order as little as 1 meal ',
              alaCarte(),
              '. Just keep in mind that a delivery fee applies to each delivery: $10 for Manhattan, Brooklyn, and LIC, and a bit more for NJ, Westchester, and Long Island. Many clients order meals for the full week when working toward health or fitness goals. Others order as needed; typically 5–10 individual meals or 2–3 family platters for a household.',
            ],
          ],
        },
      ],
    },

    { type: 'heading', level: 'band', text: 'Delivery' },
    {
      type: 'faq',
      items: [
        {
          q: 'Where do you serve? How do you deliver?',
          a: [
            [
              'We currently serve Brooklyn, Manhattan, Long Island City, Long Island, Westchester, some parts of CT and NJ. Our drivers deliver your food in person to your door. We can also serve addresses outside this delivery zone and can price delivery for you. Contact ',
              email('claire@getsquarefare.com'),
              ' for this information.',
            ],
          ],
        },
        {
          q: 'When do you deliver?',
          a: [
            `We cook on Sundays, Mondays, and Wednesdays (that’s how fresh your food is!) and typically deliver ${DELIVERY.short}. We recommend not planning to eat a Square Fare meal those evenings – we try to get to you before dinnertime, but NYC traffic is real. For NYC metropolitan customers, we can also do morning deliveries on Monday and Tuesday mornings for an extra charge.`,
            'When you check out, the list of possible options will be made available and you can select the one or multiple delivery options you would like.',
            'Please note our cut off times, order by:',
            {
              list: DELIVERY.cutoffs.map((c) => c.replace('Order by ', '')),
            },
            [
              'If you have any questions about your order, reach out anytime to Claire: ',
              email('claire@getsquarefare.com'),
              '.',
            ],
          ],
        },
        {
          q: 'Do I have to be home for delivery?',
          a: [
            'No, you don’t have to be home. Meals are delivered to your door within a scheduled window. You can leave delivery instructions at checkout and the drivers will follow these. It is recommended to refrigerate your meals as soon as possible after arrival. We aim to be environmentally-conscious and so keep our packaging minimal and our delivery routes short. Your meals will come in a paper bag. If you’d like ice packs added to your bag, just let us know by leaving a note at checkout.',
          ],
        },
        {
          q: 'What if my delivery is late or missing?',
          a: [
            'If there are any issues with your delivery, you can contact the Square Fare team directly. They work quickly to resolve problems and ensure you receive your meals. For prompt response, text Claire: (917) 679-5306.',
          ],
        },
      ],
    },

    { type: 'heading', level: 'band', text: 'Catering' },
    {
      type: 'faq',
      items: [
        {
          q: 'Can you bring servingware and serving cutlery?',
          a: ['Absolutely! Just make the request when you send us your spec.'],
        },
        {
          q: 'Can you accommodate allergies?',
          a: [
            'Yes, we do this all the time. Just let us know what the dietary constraints are. We will share our menu and ingredients for sign-off so you know exactly what will be served.',
          ],
        },
        {
          q: 'What’s your pricing?',
          a: [
            'We can price your meeting or event as soon as we know more about what you’re looking for. Send us the date and time, the theme, the number of people being served, the type of food you are looking for, your budget, whether clean-up is involved, whether servingware is needed, and any other details for the event. Then, we will review and reach out with a proposed menu and pricing. We’ve done wellness retreats, corporate lunches, new product launch parties, catering for photo shoots and more!',
          ],
        },
        {
          q: 'What are your delivery zones?',
          a: [
            'We currently serve Brooklyn, Manhattan, Long Island City, Long Island, Westchester, and some parts of CT and NJ.',
          ],
        },
        {
          q: 'How far in advance do I need to place the order?',
          a: [
            'While we generally ask for a few days so that we can schedule and plan in collaboration with you, we are able to do a fast turnaround. Make sure to let us know the date of your event when you reach out.',
          ],
        },
      ],
    },

    { type: 'heading', level: 'band', text: 'RDNs + Nutritionists' },
    {
      type: 'faq',
      items: [
        {
          q: 'How do you work with nutritionists?',
          a: [
            'We support your work by tailoring meals to the guidelines you have for your clients (food restrictions, calories, macros). This is usually helpful for those clients who can’t / don’t like to cook. For this, simply email us together with your client and provide those guidelines. We will then respond by explaining how to order. We will then adapt your client’s meals according to your specs for them.',
          ],
        },
        {
          q: 'What if my client only wants 3 or 4 meals per week?',
          a: [
            [
              'That’s absolutely fine! Your client can buy ',
              alaCarte(),
              ' or get a ',
              subscription(),
              '; we have no minimums. All orders have a delivery fee, so we typically see people buying more than 1 meal at a time, but it’s not a requirement.',
            ],
          ],
        },
        {
          q: 'My client has an eating disorder, I would rather not show any of the calories, macros and other data.',
          a: [
            'That’s absolutely something we can accommodate. In fact, many of our clients have learned what their ideal portions look like from eating their Square Fare meals and then use those portions as a reference when they cook or go out. This learning experience helps clients to not rely on the numbers and develop intuition for what their plate should look like.',
          ],
        },
        {
          q: 'Once I give you the specs for my client, will they be interacting with you or me?',
          a: [
            'That is completely up to you! If you’d rather check in with your client and inform us of any order frequency changes, that works! Or we can do it if you prefer.',
            'We do send general emails with menu information twice a week (your client can opt-out at any time) and give your client’s phone number to our delivery drivers so they can reach them if needed during the delivery cycle.',
          ],
        },
        {
          q: 'Sounds super easy! So what does my client need to do exactly?',
          a: [
            [
              'It is! All your client has to do is select their meals when buying ',
              alaCarte(),
              ' or ',
              subscription('select a subscription'),
              '. We will coordinate with you to get the specs, and that’s it!',
            ],
            'It’s simple for you and the client, but a lot of work happens in the background. We separately analyze the nutritional value of every ingredient from every one of our chef’s recipes. We then group ingredients into categories: protein (animal or plant-based), veggies, starch, and sauce. And that’s where the magic happens. We adjust those category levels based on your client’s individual needs so each meal matches up to their nutritional requirements. We make substitutions as necessary.',
          ],
        },
        {
          q: 'Do you have a guide I can share with my clients?',
          a: [
            [
              'Yes, we have a PDF guide that you can email your clients so they can get to know how Square Fare works and get their FAQs answered. ',
              { label: 'Access and download the PDF here.', href: DOCS.aboutGuide },
            ],
          ],
        },
      ],
    },

    { type: 'heading', level: 'band', text: 'Programs' },
    { type: 'heading', level: 'sub', text: 'Children’s Meals' },
    {
      type: 'faq',
      items: [
        {
          q: 'I want my child to eat healthy...and also not have to prepare lunch every day! How do I get started?',
          a: [
            [
              'We have a ',
              { label: 'children’s meal program', to: '/daycare' },
              ' and work with schools and / or parents on this. If you have any questions, reach out to Claire, she will share all about our program: ',
              email('claire@getsquarefare.com'),
              ', (917) 679-5306',
            ],
          ],
        },
        {
          q: 'Is the service organized by the parents or the daycare?',
          a: [
            'We work with both! The parents will sign up to the service and we will coordinate with the daycare to make sure we know the schedules and the best way to deliver the food every day.',
          ],
        },
        {
          q: 'My child has allergies',
          a: [
            [
              'We can adapt their meal, please let Claire know: ',
              email('claire@getsquarefare.com'),
              ', (917) 679-5306',
            ],
          ],
        },
        {
          q: 'Do all parents need to sign up?',
          a: [
            'No, but the more families we serve at a daycare, the lower the delivery fee.',
          ],
        },
      ],
    },
    { type: 'heading', level: 'sub', text: 'The Runners Program' },
    {
      type: 'faq',
      items: [
        {
          q: 'Love this! How do I get started in the program?',
          a: [
            [
              'Start by taking our 3-mins ',
              quiz(),
              '. We will reach out to schedule a discovery call with Amity.',
            ],
          ],
        },
        {
          q: 'Are these a subscription program?',
          a: [
            [
              'We have ',
              { label: 'subscription programs', to: '/meal-plans-for-runners' },
              ' that renew monthly (cancel at any time), as well as 12- and 16-week programs.',
            ],
          ],
        },
        {
          q: 'How do you tailor my food?',
          a: [
            'We get your specs from Amity. From there, we determine how much meat (animal or plant-based), veggies, starch, and sauce you need in every meal to hit those specs. How do we do that?',
            'We analyze every recipe and portion your meals one by one. We also make substitutions for allergies and dislikes.',
          ],
        },
        {
          q: 'I have a question about something else.',
          a: [
            [
              'Let’s hear it! Reach out to Claire: ',
              email('claire@getsquarefare.com'),
              ' or Amity: ',
              email('eat.to.run.nutrition@gmail.com'),
              ' at any time.',
            ],
          ],
        },
      ],
    },

    { type: 'heading', level: 'band', text: 'Sustainability' },
    {
      type: 'faq',
      items: [
        {
          q: 'Is your packaging recyclable?',
          a: [
            'Yes! Everything we use is recyclable. We aim to be as environmentally conscious as we can!',
          ],
        },
        {
          q: 'What do you mean by zero-waste operation?',
          a: [
            'We calculate the food needs of every individual person. Once we have everyone’s orders, our chef will work with our local suppliers to source the quantities we need for every productions. That way, we buy only what’s needed to make food that day. No food goes to waste, and your food is always made from the freshest ingredients.',
          ],
        },
      ],
    },

    {
      type: 'ctaGroup',
      heading: 'Still Have Questions?',
      actions: [{ label: 'Contact Us', to: '/contact-form-general' }],
    },
  ],
};

export default page;
